import {
	useQuery,
	useInfiniteQuery,
	useQueryClient,
	useMutation,
} from "react-query";
import { toast } from "react-toastify";
import API from "./utils/api";
import {
	BankReportPaginator,
	CreatePayrollDto,
	DownloadReportQueryDto,
	FileDownloadLink,
	MigrationDto,
	PayPeriod,
	PayrollRuns,
	PayrollRunsPaginator,
	PayrollRunStatusObject,
	PayrollSummaryApiResponse,
	PayslipPaginator,
	QueryCompanyPayslipsDto,
	QueryEmployeePayslipsDto,
	QueryParam,
	QueryPayrollDto,
	QueryReportDto,
	SalaryAdvanceReportPaginator,
	SalaryReportPaginator,
	SavingSchemeReportResponsePaginator,
	SearchCompanyPayslipsDto,
	SearchReportDto,
	SSFResponsePaginator,
	TaxReportPaginator,
	PayeSummaryReportPaginator,
	PayeSummaryPaginator,
} from "../models/payroll.dto";
import ENDPOINTS from "./utils/endpoints";

export function useGetEmployeePaysips(employeeId: number, options: QueryParam) {
	const url = ENDPOINTS.PAYROLL.GET_EMPLOYEE_PAYSLIPS;
	const request = (options: QueryParam) =>
		API.PayrollAPI.getEmployeePayslips(employeeId, options);

	const { data, isLoading, error, refetch } = useQuery<PayslipPaginator, Error>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetApprovedEmployeePaysips(
	employeeId: number,
	options: QueryParam,
) {
	const url = ENDPOINTS.PAYROLL.GET_EMPLOYEE_PAYSLIPS;
	const request = (options: QueryParam) =>
		API.PayrollAPI.getApprovedEmployeePayslips(employeeId, options);

	const { data, isLoading, error, refetch } = useQuery<PayslipPaginator, Error>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetCompanyPayslips(
	companyId: number,
	payrollId: number,
	options?: QueryCompanyPayslipsDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_COMPANY_PAYSLIPS;
	const request = (options: QueryCompanyPayslipsDto) =>
		API.PayrollAPI.getCompanyPayslips(companyId, payrollId, options);

	const { data, isLoading, error, refetch } = useQuery<PayslipPaginator, Error>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetCompanyPayslipsSearch(companyId: number) {
	return useMutation(
		(options?: SearchCompanyPayslipsDto) =>
			API.PayrollAPI.searchCompanyPayslips(companyId, options),
		{
			onSuccess: (response) => {
				//toast.success('Record created successfully');
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetPayrollRunParams(id: number, options: QueryParam) {
	const url = ENDPOINTS.PAYROLL.GET_PAYROLL_RUN_PARAMS;
	const request = API.PayrollAPI.getPayrollRunParams(id, options);

	const { data, isLoading, error } = useQuery<{ data: PayrollRuns }, Error>(
		[url, id],
		() => request,
		{
			enabled: !!id,
		},
	);

	return {
		data: data,
		isLoading,
		error,
	};
}

export function useGetCompanyPayrollRuns(options?: QueryPayrollDto) {
	const url = ENDPOINTS.PAYROLL.GET_COMPANY_PAYROLL_RUNS;
	const request = (options: QueryPayrollDto) =>
		API.PayrollAPI.getCompanyPayrollRuns(options);

	const { data, isLoading, error, refetch } = useQuery<
		PayrollRunsPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useRunPayrollParams() {
	const queryClient = useQueryClient();

	return useMutation(
		({ id, options }: { id: number; options: QueryParam }) =>
			API.PayrollAPI.runPayrollParams(id, options),
		{
			onSuccess: () => {
				toast.success("Payroll run submitted successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PAYROLL.GET_COMPANY_PAYROLL_RUNS,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useCreatePayrollRun() {
	const queryClient = useQueryClient();

	return useMutation(
		({
			payload,
			options,
		}: {
			payload: CreatePayrollDto;
			options: QueryParam;
		}) => API.PayrollAPI.createPayrollRun(payload, options),
		{
			onSuccess: () => {
				toast.success("Record created successfully");
				queryClient.invalidateQueries(
					ENDPOINTS.PAYROLL.GET_COMPANY_PAYROLL_RUNS,
				);
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetPayrollExcell() {
	return useMutation(
		({
			companyId,
			payPeriodId,
			options,
		}: {
			companyId: number;
			payPeriodId: number;
			options: QueryParam;
		}) => API.PayrollAPI.getPayrollExcell(companyId, payPeriodId, options),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetRecentPayrollRun(id: number, options: QueryParam) {
	const url = ENDPOINTS.PAYROLL.GET_RECENT_PAYROLL_RUN;
	const request = API.PayrollAPI.getRecentPayrollRun(id, options);

	const { data, isLoading, error, refetch } = useQuery<
		{ data: PayPeriod },
		Error
	>([url, id], () => request, {
		enabled: false,
	});

	return {
		data: data,
		isLoading,
		error,
		refetch,
	};
}

export function useGetEmployeePayslipPdf() {
	return useMutation(
		({
			employeeId,
			payPeriodId,
		}: {
			employeeId: number;
			payPeriodId: number;
		}) => API.PayrollAPI.getEmployeePayslip(employeeId, payPeriodId),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useRunPayrollStatus(id: number, options: QueryParam) {
	const url = ENDPOINTS.PAYROLL.GET_PAYROLL_RUN_STATUS;
	const request = API.PayrollAPI.getPayrollRunStatus(id, options);

	const { data, isLoading, error } = useQuery<
		{ data: PayrollRunStatusObject },
		Error
	>([url, id], () => request, {
		enabled: !!id,
	});

	return {
		data: data,
		isLoading,
		error,
	};
}

export function useApprovePayroll() {
	return useMutation(
		({ id, options }: { id: number; options: QueryParam }) =>
			API.PayrollAPI.approvePayroll(id, options),
		{
			onSuccess: () => {
				toast.success("Publish payslips successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetRunMigraton() {
	const url = ENDPOINTS.PAYROLL.RUN_MIGRATION;
	const request = API.PayrollAPI.runMigration();

	const { data, isLoading, error } = useQuery<MigrationDto, Error>(
		[url],
		() => request,
	);

	return {
		data: data,
		isLoading,
		error,
	};
}

// Resolves a report request without calling the API. Report hooks use it when
// there is no company or pay period yet; `enabled` alone isn't enough because
// useManageTableWithSearchParams calls refetch(), which ignores it.
const emptyReport = (data: any = []): Promise<any> => Promise.resolve({ data });

export function useGetSSFReport(
	companyId: number,
	ssfName: string,
	options?: QueryReportDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_SSF_REPORT;

	const request = (options: QueryReportDto) =>
		!ssfName || !companyId || !options?.payPeriodId
			? emptyReport()
			: API.PayrollAPI.getSSFReport(companyId, ssfName, options);

	const { data, isLoading, error, refetch } = useQuery<
		SSFResponsePaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption, enabled: false },
	);

	return { data, isLoading, error, refetch };
}

export function useGetSalaryReport(
	companyId: number,
	options?: QueryReportDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_SALARY_REPORT;
	const request = (options: QueryReportDto) =>
		!companyId || !options?.payPeriodId
			? emptyReport()
			: API.PayrollAPI.getSalaryReport(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		SalaryReportPaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetSavingSchemeReport(
	companyId: number,
	options?: QueryReportDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_SAVING_SCHEME_REPORT;
	const request = (options: QueryReportDto) =>
		!companyId || !options?.payPeriodId
			? emptyReport()
			: API.PayrollAPI.getSavingSchemeReport(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		SavingSchemeReportResponsePaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetBankReport(
	companyId: number,
	payPeriodId: number,
	options?: QueryReportDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_BANK_REPORT;
	const request = (options: QueryReportDto) =>
		!companyId || !payPeriodId
			? emptyReport()
			: API.PayrollAPI.getBankReport(companyId, payPeriodId, options);

	// payPeriodId goes in the path, not in options, so it must be in the key too
	const { data, isLoading, error, refetch } = useQuery<
		BankReportPaginator,
		Error
	>(
		[url, options, companyId, payPeriodId],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useGetDownloadReport() {
	return useMutation(
		({ options }: { options: DownloadReportQueryDto }) =>
			API.PayrollAPI.downloadReport(options),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetMyPayrollPdf() {
	return useMutation(
		({ payPeriodId, options }: { payPeriodId: number; options: QueryParam }) =>
			API.PayrollAPI.getMyPayrollPdf(payPeriodId, options),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetEmployeePayrollPdf() {
	return useMutation(
		({
			companyId,
			payPeriodId,
			options,
		}: {
			companyId: number;
			payPeriodId: number;
			options: QueryParam;
		}) => API.PayrollAPI.getEmployeePayrollPdf(companyId, payPeriodId, options),
		{
			onSuccess: () => {},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetTaxReport(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_TAX_REPORT;

	const request = (options: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = options;
		if (!companyId || !payPeriodId) return emptyReport();
		return API.PayrollAPI.getTaxReport(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<
		TaxReportPaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey, pageParam }) => {
			const mergedOptions = Object.assign({}, queryKey[1], pageParam);
			return request(mergedOptions);
		},
		{
			...API.Handlers.pageParamOption,
			enabled: !!options?.payPeriodId,
		},
	);

	return { data, isLoading, error, refetch };
}

export function useGetSalaryAdvanceReport(
	companyId: number,
	options?: QueryParam & { payPeriodId: number; adminId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_SALARY_ADVANCE_REPORT;

	const request = (
		options: QueryParam & { payPeriodId: number; adminId: number },
	) => {
		const { payPeriodId, ...query } = options;
		if (!companyId || !payPeriodId) return emptyReport();
		return API.PayrollAPI.getSalaryAdvanceReport(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<
		SalaryAdvanceReportPaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey, pageParam }) => {
			const mergedOptions = Object.assign({}, queryKey[1], pageParam);
			return request(mergedOptions);
		},
		{
			...API.Handlers.pageParamOption,
			enabled: !!options?.payPeriodId && !!options?.adminId,
		},
	);

	return { data, isLoading, error, refetch };
}

export function useGetSalaryReportSearch(
	companyId: number,
	options?: SearchReportDto,
) {
	const url = ENDPOINTS.PAYROLL.GET_COMPANY_SALARY_SEARCH_REPORT;
	const request = (options: SearchReportDto) =>
		API.PayrollAPI.getSalaryReportSearch(companyId, options);

	const { data, isLoading, error, refetch } = useQuery<
		SalaryReportPaginator,
		Error
	>(
		[url, options],
		({ queryKey, pageParam }) =>
			request(Object.assign({}, queryKey[1], pageParam)),
		{ ...API.Handlers.pageParamOption },
	);

	return { data, isLoading, error, refetch };
}

export function useMarkPayrollRunDeleted() {
	return useMutation(
		({
			companyId,
			payrollRunId,
			query,
		}: {
			companyId: number;
			payrollRunId: number;
			query: QueryParam;
		}) => API.PayrollAPI.markPayrollRunDeleted(companyId, payrollRunId, query),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useDeletePayrollRun() {
	return useMutation(
		({
			companyId,
			payrollRunId,
			query,
		}: {
			companyId: number;
			payrollRunId: number;
			query: QueryParam;
		}) => API.PayrollAPI.deletePayrollRun(companyId, payrollRunId, query),
		{
			onSuccess: () => {
				toast.success("Record deleted successfully");
			},
			onError: API.Handlers.onErrorHandler,
		},
	);
}

export function useGetPayrollSummary(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY;

	const request = (opts: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = opts;
		if (!companyId || !payPeriodId) return emptyReport({ summaryItems: [] });
		return API.PayrollAPI.getPayrollSummary(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<
		PayrollSummaryApiResponse,
		Error
	>(
		[url, options, companyId],
		({ queryKey }) => {
			const params = queryKey[1] as QueryParam & { payPeriodId: number };
			return request(params);
		},
		{
			enabled: !!options?.payPeriodId,
		},
	);

	return {
		data: data?.data?.summaryItems || [], // flatten here for easier consumption
		isLoading,
		error,
		refetch,
	};
}

export function useGetPayrollSummaryExcel(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY_EXCEL;

	const request = (options: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = options;
		return API.PayrollAPI.getPayrollSummaryExcel(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<FileDownloadLink, Error>(
		[url, options],
		({ queryKey, pageParam }) => {
			const mergedOptions = Object.assign({}, queryKey[1], pageParam);
			return request(mergedOptions);
		},
		{
			enabled: !!options?.payPeriodId,
		},
	);

	return { data, isLoading, error, refetch };
}

export function useGetSalaryAdvanceReportExcel(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_SALARY_ADVANCE_REPORT_EXCEL;

	const request = (options: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = options;
		return API.PayrollAPI.getPayrollSummaryExcel(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<FileDownloadLink, Error>(
		[url, options],
		({ queryKey, pageParam }) => {
			const mergedOptions = Object.assign({}, queryKey[1], pageParam);
			return request(mergedOptions);
		},
		{
			enabled: !!options?.payPeriodId,
		},
	);

	return { data, isLoading, error, refetch };
}

export function useGetPayrollSummaryPdf(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_PAYROLL_SUMMARY_PDF;

	const request = (options: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = options;
		return API.PayrollAPI.getPayrollSummaryPdf(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<FileDownloadLink, Error>(
		[url, options],
		({ queryKey, pageParam }) => {
			const mergedOptions = Object.assign({}, queryKey[1], pageParam);
			return request(mergedOptions);
		},
		{
			enabled: !!options?.payPeriodId,
		},
	);

	return { data, isLoading, error, refetch };
}

export function useGetPayeSummaryReport(
	companyId: number,
	options?: QueryParam & { payPeriodId: number },
) {
	const url = ENDPOINTS.PAYROLL.GET_PAYE_SUMMARY;

	const request = (opts: QueryParam & { payPeriodId: number }) => {
		const { payPeriodId, ...query } = opts;
		if (!companyId || !payPeriodId) return emptyReport();
		return API.PayrollAPI.getPayeSummary(companyId, payPeriodId, query);
	};

	const { data, isLoading, error, refetch } = useQuery<
		PayeSummaryPaginator,
		Error
	>(
		[url, options, companyId],
		({ queryKey }) => {
			const params = queryKey[1] as QueryParam & { payPeriodId: number };
			return request(params);
		},
		{
			enabled: !!options?.payPeriodId, // ← no pageParamOption
		},
	);

	return { data, isLoading, error, refetch };
}
